import React, { useContext, useState } from 'react';
import { Alert, Spinner } from 'reactstrap';
import AuthorizationContext from '../../_helper/Authorization/index';
import EmailVerificationContext from '../../_helper/EmailVerification/index';
import axiosConfig from '../../Config/axios';
import { getSecureAuthenticationRoute } from '../../Route/RouteIndex';
import { toast } from 'react-toastify';
import './EmailVerificationPopup.scss';

const EmailVerificationPopup: React.FC = () => {
    const authContext = useContext(AuthorizationContext);
    const emailVerificationContext = useContext(EmailVerificationContext);

    const [verificationCode, setVerificationCode] = useState<string>('');
    const [requestingCode, setRequestingCode] = useState<boolean>(false);
    const [codeRequested, setCodeRequested] = useState<boolean>(false);
    const [verifying, setVerifying] = useState<boolean>(false);

    if (!authContext || !emailVerificationContext) {
        return null;
    }

    const { errorHandler, setAuthorizedUser } = authContext;
    const isEmailVerified = authContext.authorizedUser?.email_verified;
    const isDismissed = emailVerificationContext.isDismissed;

    // Don't show popup if email is verified or popup is dismissed
    if (isEmailVerified || isDismissed) {
        return null;
    }

    const handleRequestCode = async () => {
        setRequestingCode(true);
        axiosConfig.post(getSecureAuthenticationRoute('request-verification-code'))
            .then(() => {
                setCodeRequested(true);
                toast.success('Verification code sent to your email', {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 3000,
                });
            })
            .catch((error: any) => {
                errorHandler(error);
            })
            .finally(() => {
                setRequestingCode(false);
            });
    };

    const handleVerifyCode = async () => {
        if (!verificationCode.trim()) {
            toast.error('Please enter the verification code', {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: 2000,
            });
            return;
        }

        setVerifying(true);
        axiosConfig.post(
            getSecureAuthenticationRoute('verify-email'),
            { code: verificationCode }
        )
            .then(() => {
                // Refresh user session data to get updated email_verified status
                emailVerificationContext.refreshVerificationStatus();

                toast.success('Email verified successfully!', {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: 3000,
                });

                // Dismiss the popup
                emailVerificationContext.setIsDismissed(true);
            })
            .catch((error: any) => {
                errorHandler(error);
                setVerificationCode('');
            })
            .finally(() => {
                setVerifying(false);
            });
    };

    const handleClose = () => {
        emailVerificationContext.setIsDismissed(true);
    };

    return (
        <div className="email-verification-popup-container">
            <Alert color="warning" className="email-verification-alert">
                <div className="email-verification-content">
                    <div className="email-verification-message">
                        <strong>Email Verification Required</strong>
                        <p>
                            Please verify your email address to unlock all features.
                            {authContext.authorizedUser?.email && (
                                <> We'll send a code to <strong>{authContext.authorizedUser.email}</strong></>
                            )}
                        </p>
                    </div>

                    {!codeRequested ? (
                        <div className="email-verification-actions">
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={handleRequestCode}
                                disabled={requestingCode}
                            >
                                {requestingCode ? (
                                    <>
                                        <Spinner size="sm"  /> Sending...
                                    </>
                                ) : (
                                    'Send Verification Code'
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="email-verification-code-input">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Enter verification code"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value)}
                                disabled={verifying}
                                maxLength={10}
                            />
                            <button
                                className="btn btn-sm btn-primary ms-2"
                                onClick={handleVerifyCode}
                                disabled={verifying || !verificationCode.trim()}
                            >
                                {verifying ? (
                                    <>
                                        <Spinner size="sm" color="dark" /> Verifying...
                                    </>
                                ) : (
                                    'Verify'
                                )}
                            </button>
                            <button
                                className="btn btn-sm btn-link ms-2"
                                onClick={() => setCodeRequested(false)}
                            >
                                Back
                            </button>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleClose}
                />
            </Alert>
        </div>
    );
};

export default EmailVerificationPopup;

















